import { useEffect, useRef, useState } from 'react';
import { bookingChatChannelName, getEcho, isEchoConfigured } from '../services/echoService';

export const CHAT_CONNECTION = {
  POLLING: 'polling',
  CONNECTING: 'connecting',
  LIVE: 'live',
};

const roleTypingLabel = (role) => {
  if (role === 'admin') return 'Admin';
  if (role === 'vendor') return 'Vendor';
  if (role === 'client') return 'Client';
  return 'Someone';
};

const formatTypingText = (typers) => {
  if (!typers.length) return '';
  const labels = typers.map((t) => roleTypingLabel(t.role));
  if (labels.length === 1) return `${labels[0]} is typing…`;
  if (labels.length === 2) return `${labels[0]} and ${labels[1]} are typing…`;
  return 'Several people are typing…';
};

/**
 * Subscribe to live booking chat events via Laravel Reverb (falls back to polling when unconfigured).
 */
export const useBookingChatRealtime = (
  bookingId,
  { onMessage, enabled = true, currentUserId = null } = {}
) => {
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const [connectionStatus, setConnectionStatus] = useState(
    isEchoConfigured() ? CHAT_CONNECTION.CONNECTING : CHAT_CONNECTION.POLLING
  );
  const [typingByUser, setTypingByUser] = useState({});

  useEffect(() => {
    if (!enabled || !bookingId || !isEchoConfigured()) {
      setConnectionStatus(CHAT_CONNECTION.POLLING);
      setTypingByUser({});
      return undefined;
    }

    const echo = getEcho();
    if (!echo) {
      setConnectionStatus(CHAT_CONNECTION.POLLING);
      return undefined;
    }

    const pusher = echo.connector?.pusher;
    const channelName = bookingChatChannelName(bookingId);
    const channel = echo.private(channelName);

    const markLive = () => setConnectionStatus(CHAT_CONNECTION.LIVE);
    const markConnecting = () => setConnectionStatus(CHAT_CONNECTION.CONNECTING);
    const markPolling = () => setConnectionStatus(CHAT_CONNECTION.POLLING);

    if (pusher?.connection?.state === 'connected') {
      markLive();
    } else {
      markConnecting();
    }

    const onConnected = () => markLive();
    const onDisconnected = () => markConnecting();
    const onUnavailable = () => markPolling();

    pusher?.connection?.bind('connected', onConnected);
    pusher?.connection?.bind('disconnected', onDisconnected);
    pusher?.connection?.bind('unavailable', onUnavailable);
    pusher?.connection?.bind('failed', onUnavailable);

    channel.listen('.message.sent', (payload) => {
      const message = payload?.message;
      if (message && onMessageRef.current) {
        onMessageRef.current(message);
      }
    });

    channel.listen('.typing', (payload) => {
      if (!payload?.user_id || payload.user_id === currentUserId) {
        return;
      }
      setTypingByUser((prev) => ({
        ...prev,
        [payload.user_id]: {
          role: payload.sender_role,
          until: Date.now() + 4000,
        },
      }));
    });

    if (typeof channel.subscribed === 'function') {
      channel.subscribed(() => markLive());
    }
    if (typeof channel.error === 'function') {
      channel.error(() => markPolling());
    }

    return () => {
      channel.stopListening('.message.sent');
      channel.stopListening('.typing');
      echo.leave(channelName);
      pusher?.connection?.unbind('connected', onConnected);
      pusher?.connection?.unbind('disconnected', onDisconnected);
      pusher?.connection?.unbind('unavailable', onUnavailable);
      pusher?.connection?.unbind('failed', onUnavailable);
      setTypingByUser({});
    };
  }, [bookingId, enabled, currentUserId]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTypingByUser((prev) => {
        const now = Date.now();
        const next = Object.fromEntries(
          Object.entries(prev).filter(([, value]) => value.until > now)
        );
        return Object.keys(next).length === Object.keys(prev).length ? prev : next;
      });
    }, 500);

    return () => clearInterval(timer);
  }, []);

  const activeTypers = Object.values(typingByUser).filter((t) => t.until > Date.now());
  const typingText = formatTypingText(activeTypers);

  return { connectionStatus, typingText };
};

export const appendBookingChatMessage = (queryClient, bookingId, isAdmin, message) => {
  if (!message?.id) {
    return;
  }

  const queryKey = ['bookingChat', bookingId, isAdmin];
  queryClient.setQueryData(queryKey, (old) => {
    if (!old?.data) {
      return old;
    }
    const exists = old.data.some((m) => m.id === message.id);
    if (exists) {
      return old;
    }
    return {
      ...old,
      data: [...old.data, message],
    };
  });
};
